import type { ActivityDTO } from 'src/types/dto/activity.dto';
import type { RescheduleSuggestionDTO } from 'src/types/dto/reschedule-suggestion.dto';

import { useEffect, useMemo, useRef, useState } from 'react';

import { mergeRescheduleSuggestion } from 'src/components/shuffle/inspector-availability-grid/group-reschedule-progress-changes';

import { enrichRescheduleSuggestion } from './enrich-reschedule-suggestion';
import {
  getEnrichmentKey,
  orderRescheduleSuggestionsByCompletion,
  type RescheduleSuggestionViewModel,
} from './reschedule-suggestions-ordering';

/**
 * Owns the data-fetching (enrichment) for every reschedule suggestion so the
 * list can be ordered by which cards finished loading first. Extracting this
 * from the card keeps the card presentational and lets the parent render the
 * fully-fetched cards at the top.
 */
export const useRescheduleSuggestionsData = (
  suggestions: RescheduleSuggestionDTO[],
  currentActivity: ActivityDTO
): RescheduleSuggestionViewModel[] => {
  const [dataById, setDataById] = useState<Map<number, RescheduleSuggestionDTO>>(new Map());
  const [loadingById, setLoadingById] = useState<Map<number, boolean>>(new Map());
  const [completionOrder, setCompletionOrder] = useState<number[]>([]);
  const enrichedKeyById = useRef<Map<number, string>>(new Map());
  const inFlightKeyById = useRef<Map<number, string>>(new Map());
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (suggestions.length === 0) {
      setDataById(new Map());
      setLoadingById(new Map());
      setCompletionOrder([]);
      enrichedKeyById.current.clear();
      inFlightKeyById.current.clear();
      return;
    }

    setDataById((current) => {
      const next = new Map(current);
      suggestions.forEach((suggestion) => {
        next.set(
          suggestion.solutionId,
          mergeRescheduleSuggestion(next.get(suggestion.solutionId), suggestion)
        );
      });
      return next;
    });
  }, [suggestions]);

  useEffect(() => {
    dataById.forEach((suggestion, solutionId) => {
      const enrichmentKey = getEnrichmentKey(suggestion, currentActivity.id);

      if (
        enrichedKeyById.current.get(solutionId) === enrichmentKey ||
        inFlightKeyById.current.get(solutionId) === enrichmentKey
      ) {
        return;
      }

      inFlightKeyById.current.set(solutionId, enrichmentKey);
      setLoadingById((current) => new Map(current).set(solutionId, true));

      enrichRescheduleSuggestion(suggestion, currentActivity)
        .then((enrichedSuggestion) => {
          if (
            !isMountedRef.current ||
            inFlightKeyById.current.get(solutionId) !== enrichmentKey
          ) {
            return;
          }

          enrichedKeyById.current.set(solutionId, enrichmentKey);

          setDataById((current) => {
            const next = new Map(current);
            next.set(
              solutionId,
              mergeRescheduleSuggestion(next.get(solutionId), enrichedSuggestion)
            );
            return next;
          });

          setCompletionOrder((order) =>
            order.includes(solutionId) ? order : [...order, solutionId]
          );
        })
        .catch((error) => {
          console.error('Failed to enrich reschedule suggestion', error);
        })
        .finally(() => {
          const stillOwnsRequest =
            inFlightKeyById.current.get(solutionId) === enrichmentKey;

          if (stillOwnsRequest) {
            inFlightKeyById.current.delete(solutionId);
          }

          if (isMountedRef.current && stillOwnsRequest) {
            setLoadingById((current) => new Map(current).set(solutionId, false));
          }
        });
    });
  }, [currentActivity, dataById]);

  return useMemo(
    () =>
      orderRescheduleSuggestionsByCompletion(
        suggestions,
        dataById,
        completionOrder,
        loadingById
      ),
    [completionOrder, dataById, loadingById, suggestions]
  );
};
